/**
 * A single conversation row in the history drawer.
 *
 * Tap to switch, swipe left to reveal delete, long-press to rename (inline).
 */

import { useState, useRef, useCallback } from "react";
// Note: swipe-to-delete removed in favor of always-visible delete button
import { MessageSquare, Trash2, Check, X } from "lucide-react";
import { cn } from "../lib/utils";
import { formatShortDate } from "../../utils/dateFormatters";
import type { ConversationSummary } from "../types";

// ---------------------------------------------------------------------------
// Relative time formatter
// ---------------------------------------------------------------------------

function relativeTime(iso: string | null): string {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(ms / 60_000);
  if (mins < 1) return "now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) {
    const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    return dayNames[new Date(iso).getDay()];
  }
  return formatShortDate(iso);
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface ConversationItemProps {
  conversation: ConversationSummary;
  isActive: boolean;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
  onRename: (id: string, title: string) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ConversationItem({
  conversation,
  isActive,
  onSelect,
  onDelete,
  onRename,
}: ConversationItemProps) {
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState(conversation.title);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // ── Long-press to rename ──────────────────────────────────────────
  const handlePointerDown = useCallback(() => {
    longPressTimer.current = setTimeout(() => {
      setIsRenaming(true);
      setRenameValue(conversation.title);
      // Focus will be set by autoFocus on the input
    }, 600);
  }, [conversation.title]);

  const handlePointerUp = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  // ── Rename handlers ───────────────────────────────────────────────
  const handleRenameSubmit = useCallback(() => {
    const trimmed = renameValue.trim();
    if (trimmed && trimmed !== conversation.title) {
      onRename(conversation.id, trimmed);
    }
    setIsRenaming(false);
  }, [renameValue, conversation.id, conversation.title, onRename]);

  const handleRenameCancel = useCallback(() => {
    setIsRenaming(false);
    setRenameValue(conversation.title);
  }, [conversation.title]);

  const handleRenameKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") handleRenameSubmit();
      if (e.key === "Escape") handleRenameCancel();
    },
    [handleRenameSubmit, handleRenameCancel],
  );

  // ── Tap to select ─────────────────────────────────────────────────
  const handleClick = useCallback(() => {
    if (isRenaming) return;
    onSelect(conversation.id);
  }, [isRenaming, onSelect, conversation.id]);

  if (isRenaming) {
    return (
      <div className="flex items-center gap-2 px-3 py-2">
        <input
          ref={inputRef}
          type="text"
          value={renameValue}
          onChange={(e) => setRenameValue(e.target.value)}
          onKeyDown={handleRenameKeyDown}
          onBlur={handleRenameSubmit}
          autoFocus
          className="min-w-0 flex-1 rounded border border-brand/30 bg-surface px-2 py-1 text-sm text-text-primary outline-none focus:border-brand"
          maxLength={255}
        />
        <button
          type="button"
          onClick={handleRenameSubmit}
          className="flex h-8 w-8 items-center justify-center rounded text-success"
          aria-label="Confirm rename"
        >
          <Check className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={handleRenameCancel}
          className="flex h-8 w-8 items-center justify-center rounded text-text-muted"
          aria-label="Cancel rename"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(e) => e.key === "Enter" && handleClick()}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerUp}
      className={cn(
        "flex cursor-pointer items-start gap-3 bg-surface px-4 py-3",
        "active:bg-brand/5",
        isActive && "border-l-2 border-brand bg-brand/5",
      )}
    >
      <MessageSquare className="mt-0.5 h-4 w-4 shrink-0 text-text-muted" />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <p className="truncate text-sm font-medium text-text-primary">
            {conversation.title}
          </p>
          <span className="shrink-0 text-xs text-text-muted">
            {relativeTime(conversation.updated_at)}
          </span>
        </div>
        <p className="mt-0.5 text-xs text-text-muted">
          {conversation.message_count} message{conversation.message_count !== 1 ? "s" : ""}
        </p>
      </div>
      {/* Delete button — always visible */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onDelete(conversation.id);
        }}
        className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-text-muted hover:bg-error/10 hover:text-error active:bg-error/20"
        aria-label="Delete conversation"
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  );
}
